import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import { COMPONENT_ART } from './componentArt.jsx';
import PlacementExercise from './PlacementExercise.jsx';
import FollowTheData from './FollowTheData.jsx';
import ShowAndTell from './ShowAndTell.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Inside a Computer System", opened in its own tab
 * from the lesson.
 *
 * Five sections that unlock in order: work through all eight
 * components, inspect a real specification, place each part in the
 * connector it belongs to, follow a piece of data around the board -
 * which is where the hardware turns into security - and finally meet
 * the parts for real, with a teacher holding them up.
 *
 * Each component is read first - illustration and theory - and the
 * question only appears once the learner says they have finished
 * reading. That keeps the answer from being guessed off the screen
 * before the explanation has been taken in.
 *
 * A component counts as done only once its question is answered
 * correctly. Reading alone does not advance the bar, because the point
 * is whether the idea landed. Wrong answers explain themselves and can
 * be retried.
 *
 * Progress is kept in localStorage so closing the tab does not lose the
 * work. It is a practice site, not something graded, so it never
 * reaches the server.
 */

const STORE_KEY = 'cybercub.lab.pc-components';

const COMPONENTS = [
  {
    id: 'motherboard',
    name: 'Motherboard',
    icon: '🦴',
    body: 'The skeleton and nerves',
    photos: [
      {
        src: '/img/parts/motherboard.jpg',
        alt: 'A motherboard seen at an angle: a large dark circuit board carrying the square CPU socket, two pairs of long memory slots, several expansion slots, silver heatsinks and a cluster of sockets along one edge.',
      },
    ],
    what: 'The board every other part plugs into, and the wiring between them. It decides what can be connected and how fast data moves between the pieces.',
    detail: 'Holds the chipset, the slots for RAM and cards, and the connectors for storage and power. Nothing talks to anything else except through it.',
    security:
      'It also holds the firmware that runs before the operating system does. Malware that reaches there survives wiping the disk entirely.',
    quiz: {
      q: 'The CPU needs to read something out of RAM. What makes that possible?',
      options: [
        'The power supply pushes the data across',
        'The motherboard, which carries the wiring between them',
        'The graphics card passes it along',
        'They are the same component',
      ],
      answer: 1,
      explain: 'Every part talks through the board. Its buses are the roads the data travels on.',
    },
  },
  {
    id: 'cpu',
    name: 'CPU',
    icon: '🧠',
    body: 'The brains',
    photos: [
      {
        src: '/img/parts/cpu.jpg',
        alt: 'A CPU: a small square chip with a polished metal lid on top and hundreds of gold contacts underneath. Smaller and heavier than most people expect.',
      },
    ],
    what: 'Carries out instructions: fetch one, do it, fetch the next — billions of times a second. Every calculation happens here.',
    detail: 'A "core" is one worker, so eight cores means eight instructions genuinely at once. Clock speed says how fast each worker goes.',
    security:
      'Modern CPUs enforce the wall between one program and another. Flaws in that wall, like Spectre and Meltdown, were serious precisely because they undermined something everything else relies on.',
    quiz: {
      q: 'A machine has 8 cores instead of 4. What does that actually give you?',
      options: [
        'Twice the storage space',
        'Eight instructions genuinely running at the same time instead of four',
        'Each instruction runs twice as fast',
        'Twice as much memory',
      ],
      answer: 1,
      explain: 'A core is one worker. More cores means more work in parallel, not each piece of work going faster.',
    },
  },
  {
    id: 'ram',
    name: 'RAM',
    icon: '⚡',
    body: 'Short-term memory',
    photos: [
      {
        src: '/img/parts/ram.jpg',
        alt: 'A stick of RAM: a long thin green circuit board with black memory chips along it and a notch cut into the row of gold contacts, off centre so it cannot be fitted the wrong way round.',
      },
    ],
    what: 'The workspace. Whatever is running right now lives here, because reaching it is enormously faster than reaching a disk.',
    detail: 'Volatile: cut the power and it is blank. Too little RAM and the machine keeps fetching from storage instead, which is why it crawls.',
    security:
      'Passwords and encryption keys sit in RAM while a program runs. That is why investigators capture memory before switching a machine off — pull the plug and the evidence is gone.',
    quiz: {
      q: 'The power cuts out and you lose an hour of unsaved work. Why?',
      options: [
        'The hard drive was damaged by the power cut',
        'Unsaved work was only in RAM, which is wiped when power is lost',
        'The operating system deleted it on restart',
        'The CPU overheated',
      ],
      answer: 1,
      explain: 'RAM is volatile. Until work is written to storage it exists nowhere that survives a power cut.',
    },
  },
  {
    id: 'storage',
    name: 'Storage (SSD/HDD)',
    icon: '💾',
    body: 'Long-term memory',
    // Both, because the contrast is the lesson: one has nothing moving
    // inside it and the other is full of spinning metal.
    photos: [
      {
        src: '/img/parts/ssd.jpg',
        alt: 'An M.2 NVMe solid-state drive: a slim circuit board about the size of a stick of chewing gum, with chips under a black label and gold contacts at one end. Nothing inside it moves.',
        caption: 'SSD - no moving parts',
      },
      {
        src: '/img/parts/hdd.jpg',
        alt: 'A 3.5 inch hard disk drive: a heavy sealed metal box with a printed label on top and a row of connectors along one edge. Inside, metal platters spin.',
        caption: 'HDD - spinning platters',
      },
    ],
    what: 'Keeps files, programs and the operating system when the power is off. Slower than RAM, and it remembers.',
    detail: 'An SSD has no moving parts and is far quicker; a hard drive uses spinning platters and is cheaper for bulk. Many machines have both.',
    security:
      'Deleting a file usually removes the pointer, not the contents, which is why deleted data is so often recoverable — and why disks need wiping properly before they are thrown out.',
    quiz: {
      q: 'You delete a file and empty the recycle bin. Why might it still be recoverable?',
      options: [
        'A copy is always kept in RAM',
        'Deleting usually removes the pointer to the file, not the contents',
        'The file is sent to the manufacturer first',
        'Windows keeps a hidden backup of everything',
      ],
      answer: 1,
      explain: 'The space is marked reusable rather than cleared, so the contents sit there until something overwrites them.',
    },
  },
  {
    id: 'network',
    name: 'Network Adapter',
    icon: '🌐',
    body: 'The voice and ears',
    what: 'Connects the machine to other machines, by cable or wireless. Everything that leaves or arrives passes through it.',
    detail: 'Each adapter has a MAC address, a hardware identifier used on the local network before any internet address is involved.',
    security:
      'It is the door to everything remote. A machine with no network adapter can still be attacked, but somebody has to physically walk up to it.',
    quiz: {
      q: 'A machine has no network adapter at all. What follows?',
      options: [
        'It cannot be attacked in any way',
        'It can still be attacked, but somebody has to physically reach it',
        'It cannot run any software',
        'It will not switch on',
      ],
      answer: 1,
      explain: 'Removing the network removes remote attacks, not all of them. A USB stick and physical access still work.',
    },
  },
  {
    id: 'psu',
    name: 'Power Supply (PSU)',
    icon: '❤️',
    body: 'Heart and lungs',
    photos: [
      {
        src: '/img/parts/psu.jpg',
        alt: 'A power supply unit: a heavy black metal box with a large fan on one face, a mains socket and switch on another, and a thick bundle of coloured cables coming out of it.',
      },
    ],
    what: 'Converts mains electricity into the steady low voltages the parts need, and pushes it round the machine.',
    detail: 'Rated in watts. Too small and the machine restarts under load; failing ones cause crashes that look like software faults for months.',
    security:
      'Availability is one third of security. A machine that will not stay powered is as unavailable as one that has been attacked.',
    quiz: {
      q: 'A machine switches off instantly under heavy load — no error, no blue screen. The likeliest cause is:',
      options: [
        'A virus',
        'The power supply cannot deliver enough power',
        'The monitor cable',
        'Too many files on the disk',
      ],
      answer: 1,
      explain: 'Software faults normally leave something behind — an error, a log, a crash screen. A silent dead stop points at power.',
    },
  },
  {
    id: 'gpu',
    name: 'Graphics Card',
    icon: '👁️',
    body: 'Visual cortex',
    photos: [
      {
        src: '/img/parts/gpu.jpg',
        alt: 'A graphics card: a long black board carrying two large fans and a heatsink, with display sockets along the metal bracket at one end and a row of gold contacts on the bottom edge.',
      },
    ],
    what: 'Works out what every pixel on the screen should be. Built to do thousands of simple calculations simultaneously.',
    detail: 'That shape suits more than graphics: the same parallelism drives machine learning, video encoding and password cracking.',
    security:
      'Which is exactly why password cracking uses them. A GPU tries vastly more guesses per second than a CPU, and it is the reason slow hashing matters.',
    quiz: {
      q: 'Why do attackers use graphics cards to crack passwords?',
      options: [
        'They have more storage than a CPU',
        'They do thousands of simple calculations at once, so they guess far faster',
        'They can read encrypted files directly',
        'They bypass the operating system',
      ],
      answer: 1,
      explain: 'Massive parallelism is the whole point — and the reason password hashing is deliberately made slow.',
    },
  },
  {
    id: 'io',
    name: 'Input/Output',
    icon: '⌨️',
    body: 'Hands, eyes and mouth',
    what: 'Everything the machine uses to reach the outside world: keyboard, mouse, screen, speakers, USB ports.',
    detail: 'Input arrives, output leaves. It is the layer a person actually touches, and the only part most users ever think about.',
    security:
      'USB ports are a genuine attack route. A device can claim to be a keyboard and type commands faster than you could read them, which is why found USB sticks are never plugged in.',
    quiz: {
      q: 'You find a USB stick in the car park. Why is plugging it in a bad idea?',
      options: [
        'It might use too much power',
        'It can claim to be a keyboard and type commands faster than you could read them',
        'It will slow the computer down',
        'USB ports wear out quickly',
      ],
      answer: 1,
      explain: 'The machine trusts what a device says it is. A "keyboard" needs no permission to start typing.',
    },
  },
];

const SPEC_LINES = [
  { id: 's1', line: 'Intel Core i5-12400, 6 cores @ 2.5 GHz', answer: 'cpu', why: 'Cores and clock speed always describe the processor.' },
  { id: 's2', line: '16 GB DDR4-3200', answer: 'ram', why: 'DDR is memory. The number after it is how fast that memory runs.' },
  { id: 's3', line: '512 GB NVMe M.2', answer: 'storage', why: 'NVMe and M.2 are ways of connecting a solid-state drive.' },
  { id: 's4', line: 'Realtek RTL8111 Gigabit Ethernet', answer: 'network', why: 'Ethernet is wired networking, so this is the network adapter.' },
  { id: 's5', line: '650 W 80+ Bronze', answer: 'psu', why: 'Watts and an efficiency rating mean the power supply.' },
  { id: 's6', line: 'NVIDIA RTX 3060, 12 GB', answer: 'gpu', why: 'A named graphics chip with its own memory is the graphics card.' },
];

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

const save = (state) => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    /* private window, or storage blocked - the lab still works */
  }
};

export default function PcComponentsLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('components');
  const [solved, setSolved] = useState(() => new Set(saved.solved ?? []));
  const [read, setRead] = useState(() => new Set(saved.read ?? []));
  const [open, setOpen] = useState(null);
  const [picked, setPicked] = useState(null);      // answer chosen in the open panel
  const [specs, setSpecs] = useState(saved.specs ?? {});
  const [answers, setAnswers] = useState(saved.answers ?? {});

  useEffect(() => {
    save({ solved: [...solved], read: [...read], specs, answers });
  }, [solved, read, specs, answers]);

  useEffect(() => {
    document.title = 'Inside a Computer System · CyberCub Lab';
  }, []);

  const componentsDone = solved.size === COMPONENTS.length;
  const specsDone = SPEC_LINES.every((s) => specs[s.id] === s.answer);
  const exerciseDone = answers.placement === 'done';
  const traceDone = answers.trace === 'done';

  /* Stage four, not stage five: Show and Tell needs a teacher holding
     real components, and a child working at home cannot do it. The
     four on-screen stages are what the lesson can fairly insist on. */
  useFinished('/lab/pc-components', traceDone);

  const openPart = (c) => {
    setOpen(c);
    setPicked(solved.has(c.id) ? c.quiz.answer : null);
  };

  /** Marks the theory read, which is what reveals the question. */
  const markRead = (id) => setRead((prev) => new Set(prev).add(id));

  const answer = (index) => {
    setPicked(index);
    if (index === open.quiz.answer) {
      setSolved((prev) => new Set(prev).add(open.id));
    }
  };

  const TABS = [
    { id: 'components', n: 1, label: 'Components', locked: false },
    { id: 'inspection', n: 2, label: 'Inspection', locked: !componentsDone },
    { id: 'exercise', n: 3, label: 'Placement', locked: !specsDone },
    { id: 'trace', n: 4, label: 'Follow the Data', locked: !exerciseDone },
    { id: 'show', n: 5, label: 'Show and Tell', locked: !traceDone },
  ];

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Inside a Computer System</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf1-inside-a-computer" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. components ---------------- */}
      {tab === 'components' && (
        <section className="lab-section">
          <p className="lab-lead">
            Click a component to discover what it does, then answer its question. Get all{' '}
            {COMPONENTS.length} right to unlock the next section.
          </p>

          <div className="lab-progress">
            <div className="row spread">
              <strong>Progress</strong>
              <span>{solved.size} / {COMPONENTS.length}</span>
            </div>
            <div className="lab-bar">
              <span style={{ width: `${(solved.size / COMPONENTS.length) * 100}%` }} />
            </div>
          </div>

          <div className="lab-grid">
            {COMPONENTS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`lab-part ${solved.has(c.id) ? 'is-seen' : ''} ${open?.id === c.id ? 'is-open' : ''}`}
                onClick={() => openPart(c)}
              >
                <span className="lab-part-icon" aria-hidden="true">{c.icon}</span>
                <strong>{c.name}</strong>
                <span className="lab-part-body">{c.body}</span>
                {solved.has(c.id) && <span className="lab-tick" aria-label="answered correctly">✓</span>}
              </button>
            ))}
          </div>

          {componentsDone && (
            <div className="lab-done">
              <strong>All eight answered.</strong> Inspection is unlocked — move on when you are ready.
              <button type="button" className="btn btn-sm" onClick={() => setTab('inspection')}>
                Go to Inspection →
              </button>
            </div>
          )}

          {open && (
            <div className="lab-detail" role="dialog" aria-label={open.name}>
              <div className="row spread">
                <div className="row gap-sm">
                  <span className="lab-part-icon" aria-hidden="true">{open.icon}</span>
                  <div>
                    <h2>{open.name}</h2>
                    <span className="lab-part-body">{open.body}</span>
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(null)}>
                  Close
                </button>
              </div>

              {/* ---- illustration and theory ---- */}
              {/* Parts with photographs show them here instead of the
                  drawing, and storage shows two - an SSD beside a hard
                  drive - because the difference between them is the
                  point. The small chips in the other sections stay
                  drawn: a photo does not survive thumbnail size. */}
              <div className={`lab-art ${open.photos?.length > 1 ? 'is-pair' : ''}`}>
                {open.photos ? (
                  open.photos.map((ph) => (
                    <figure key={ph.src}>
                      <img src={ph.src} alt={ph.alt} loading="lazy" />
                      {ph.caption && <figcaption>{ph.caption}</figcaption>}
                    </figure>
                  ))
                ) : (() => {
                  const Art = COMPONENT_ART[open.id];
                  return Art ? <Art /> : null;
                })()}
              </div>

              <p>{open.what}</p>
              <p className="muted">{open.detail}</p>
              <div className="lab-note">
                <strong>Why it matters for security</strong>
                <p>{open.security}</p>
              </div>

              {/* The question stays hidden until the theory is marked read,
                  so the answer cannot be picked off the screen first. */}
              {!read.has(open.id) && !solved.has(open.id) ? (
                <div className="lab-readgate">
                  <p className="small muted">Read the description above, then carry on to the question.</p>
                  <button type="button" className="btn" onClick={() => markRead(open.id)}>
                    ✓ Complete — show the question
                  </button>
                </div>
              ) : (
              /* ---- the check ---- */
              <div className="lab-mcq">
                <h3>
                  {solved.has(open.id) ? '✓ ' : ''}
                  {open.quiz.q}
                </h3>
                <div className="lab-options">
                  {open.quiz.options.map((opt, i) => {
                    const isPicked = picked === i;
                    const isRight = i === open.quiz.answer;
                    const state = picked === null ? '' : isRight ? 'is-right' : isPicked ? 'is-wrong' : '';
                    return (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-option ${state} ${isPicked ? 'is-picked' : ''}`}
                        onClick={() => answer(i)}
                        disabled={solved.has(open.id)}
                      >
                        <span className="lab-option-mark">{'ABCD'[i]}</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>

                {picked !== null && (
                  <p className={`lab-feedback ${picked === open.quiz.answer ? 'is-right' : 'is-wrong'}`}>
                    {picked === open.quiz.answer
                      ? `✓ ${open.quiz.explain}`
                      : '✗ Not quite — read the description again and try another answer.'}
                  </p>
                )}

                {solved.has(open.id) && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      const next = COMPONENTS.find((c) => !solved.has(c.id));
                      if (next) openPart(next);
                      else setOpen(null);
                    }}
                  >
                    {COMPONENTS.some((c) => !solved.has(c.id)) ? 'Next component →' : 'Done'}
                  </button>
                )}
              </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. inspection ---------------- */}
      {tab === 'inspection' && (
        <section className="lab-section">
          <p className="lab-lead">
            A real specification, as a shop or a job advert would list it. Match each line to the
            part it describes.
          </p>

          <div className="lab-specs">
            {SPEC_LINES.map((s) => {
              const chosen = specs[s.id];
              const right = chosen === s.answer;
              return (
                <div key={s.id} className={`lab-spec ${chosen ? (right ? 'is-right' : 'is-wrong') : ''}`}>
                  <code>{s.line}</code>
                  <select
                    value={chosen ?? ''}
                    onChange={(e) => setSpecs((p) => ({ ...p, [s.id]: e.target.value }))}
                    aria-label={`Which part is ${s.line}`}
                  >
                    <option value="">— choose a part —</option>
                    {COMPONENTS.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {chosen && (
                    <p className="lab-why">
                      {right ? `✓ ${s.why}` : '✗ Not that one — look at the wording again.'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {specsDone && (
            <div className="lab-done">
              <strong>Every line identified.</strong> You can now read a specification the way a
              technician does.
              <button type="button" className="btn btn-sm" onClick={() => setTab('exercise')}>
                Go to Exercise →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 3. exercise ---------------- */}
      {tab === 'exercise' && (
        <PlacementExercise
          onBack={() => setTab('inspection')}
          onSolved={() => setAnswers((p) => ({ ...p, placement: 'done' }))}
        />
      )}

      {/* ---------------- 4. follow the data ---------------- */}
      {tab === 'trace' && (
        <FollowTheData
          onBack={() => setTab('exercise')}
          onSolved={() => setAnswers((p) => ({ ...p, trace: 'done' }))}
        />
      )}

      {/* ---------------- 5. in the room ---------------- */}
      {tab === 'show' && (
        <ShowAndTell
          onBack={() => setTab('trace')}
          onSolved={() => setAnswers((p) => ({ ...p, show: 'done' }))}
        />
      )}

    </div>
  );
}
